import i18next, { type i18n as I18n } from 'i18next';
import it from './locales/it.json' with { type: 'json' };
import en from './locales/en.json' with { type: 'json' };

/**
 * Catalogs and formatters (ch. 24). Keys are hierarchical; plurals use i18next's _one/_other
 * suffixes (Intl.PluralRules). Numbers, dates and money always use the ACCOUNT locale, not the
 * browser's, so app, PDF and email agree.
 */
export type Lang = 'it' | 'en';
export const LANGS: Lang[] = ['it', 'en'];
export const resources = { it: { translation: it }, en: { translation: en } } as const;
export type Catalog = typeof it;

export const langOf = (locale: string | null | undefined): Lang => (locale?.toLowerCase().startsWith('en') ? 'en' : 'it');

export function createI18n(lang: Lang): I18n {
  const inst = i18next.createInstance();
  void inst.init({
    resources,
    lng: lang,
    fallbackLng: 'en',
    initImmediate: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    showSupportNotice: false,
  });
  return inst;
}

const servers = new Map<Lang, I18n>();
/** Server-side translation (API error messages, emails). */
export function translate(lang: Lang, key: string, params?: Record<string, unknown>): string {
  let inst = servers.get(lang);
  if (!inst) {
    inst = createI18n(lang);
    servers.set(lang, inst);
  }
  return inst.t(key, params ?? {}) as string;
}

export function formatMoney(minor: number, currency: string, locale: string, opts: { cents?: boolean } = {}): string {
  // euro amounts without cents when round (boards); other currencies always with cents, uniform with Intl (R4 #6)
  const showCents = opts.cents ?? (minor % 100 !== 0 || currency !== 'EUR');
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
    useGrouping: 'always',
  } as Intl.NumberFormatOptions).format(minor / 100);
}

export function formatNumber(n: number, locale: string, maxFractionDigits = 1): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: maxFractionDigits, useGrouping: 'always' } as Intl.NumberFormatOptions).format(n);
}

/** "3 h 42 min", "7 h", "25 min" — never "3:42:10" outside the timer (LG 4.5). */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

export type DateStyle = 'weekday_day_month' | 'day_month' | 'short' | 'numeric';

/** Calendar dates ('YYYY-MM-DD') are formatted as-is, without time-zone shifts. */
export function formatDate(date: string, locale: string, style: DateStyle = 'day_month'): string {
  const d = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  const opts: Intl.DateTimeFormatOptions =
    style === 'weekday_day_month'
      ? { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
      : style === 'day_month'
        ? { day: 'numeric', month: 'long', timeZone: 'UTC' }
        : style === 'short'
          ? { day: '2-digit', month: '2-digit', timeZone: 'UTC' }
          : { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' };
  const s = new Intl.DateTimeFormat(locale, opts).format(d);
  return style === 'weekday_day_month' ? s.charAt(0).toUpperCase() + s.slice(1).replace(',', '') : s;
}

/** An instant shown in the account time zone. */
export function formatInstant(iso: string, locale: string, timeZone: string, withTime = false): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    timeZone,
  }).format(new Date(iso));
}
